import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#D12D2D", "#F4B41A", "#1C1917", "#15803D", "#3F3A36", "#B45309"];

export default function AdminReports() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/orders").then(r => setOrders(r.data));
  }, []);

  const byPayment = orders.reduce((acc, o) => { acc[o.payment_method] = (acc[o.payment_method] || 0) + o.total; return acc; }, {});
  const paymentData = Object.entries(byPayment).map(([name, value]) => ({ name: name.toUpperCase(), value: Math.round(value) }));

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Reports</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Sales & profitability</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <div className="bg-white rounded-xl p-6 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Daily revenue</div>
            <div className="h-72 mt-3">
              {stats?.daily_revenue?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.daily_revenue}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE7DD" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#78716C" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#78716C" }} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E7E5E4", fontSize: 12 }} />
                    <Bar dataKey="amount" fill="#F4B41A" radius={[8,8,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-ink-500">No data</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Revenue by payment method</div>
            <div className="h-72 mt-3">
              {paymentData.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={paymentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                      {paymentData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-ink-500">No data</div>}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-border">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Top items by quantity</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {stats?.top_items?.map((t, i) => (
              <div key={t.name} className="flex items-center justify-between p-3 bg-cream-100 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-md bg-ink text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                  <span className="font-semibold text-ink">{t.name}</span>
                </div>
                <span className="text-sm font-bold text-brand">{t.count}×</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
